import React, { useEffect, useState } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import { ThemeProvider, useTheme } from 'styled-components';
import queryString from 'query-string';

import {
  Comparison,
  CompatibleDevices,
  ContentBlockSection,
  CountryListSection,
  DealsFooter,
  DealsHeader,
  DealsTimerBar,
  FeaturesTabs,
} from '@containers';
import { Box } from '@core';
import { Card, Layout, SEO, Shape, ShapeWrapper } from '@components';
import { isClient } from '@shared/helpers';
import { TimerProvider, useCouponErrorContext } from '@context';
import { usePortal } from '@hooks';
import { getModalConfig } from '@shared/constants/pages/deals';
import { ConditionalWrapper } from '@shared/helpers';

import { theme as globalTheme } from '@theme';

export const DealsPageTemplate = ({
  location,
  showLanguagePicker = true,
  showTimer = true,
  ...props
}) => {
  const {
    comparison,
    compatibleDevices,
    countryList,
    featuresTabs,
    footerCta,
    headerCta,
    metaData,
    moneyBackGuarantee,
    newsOutlets,
    timerBar,
  } = props;

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const couponError = useCouponErrorContext();

  const { showModal, Modal: CouponErrorModalComponent } = usePortal(getModalConfig());
  const [initialCoupon] = useState(queryString.parse(location.search).coupon);
  const [invalidCoupon, setInvalidCoupon] = useState();

  useEffect(() => {
    if (invalidCoupon) {
      showModal();

      // deleting coupon query param with wrong code so it doesn't transfer to other pages when navigating
      if (isClient && initialCoupon) {
        const queryParams = queryString.parse(location.search);
        delete queryParams.coupon;
        const updatedQueryParams = queryString.stringify({ ...queryParams });
        const urlWithoutCouponQueryParam = `${location.origin}${location.pathname}?${updatedQueryParams}`;
        window.history.pushState(
          { path: urlWithoutCouponQueryParam },
          '',
          urlWithoutCouponQueryParam
        );
      }
    }
  }, [
    invalidCoupon,
    showModal,
    initialCoupon,
    location.origin,
    location.pathname,
    location.search,
  ]);

  /**
   * by priority, invalid coupon in this page can only be get through wrong code in query parameter
   * as otherwise the coupon is set to DEALS_COUPON_CODE
   * setting invalidCoupon to initialCoupon here as soon as we get the error in order to be able to show wrong code in the modal
   */
  useEffect(() => {
    if (couponError) {
      setInvalidCoupon(initialCoupon);
    }
  }, [couponError, initialCoupon]);

  const theme = useTheme();

  return (
    <React.Fragment>
      <div id="portal" />
      <SEO {...metaData(t)} />
      <Layout
        location={location}
        navProps={{ isHidden: true }}
        footerProps={{
          compact: true,
          variant: theme.variants?.footer,
        }}
        wrapperProps={{
          backgroundColor: 'backgroundColor',
        }}
      >
        <CouponErrorModalComponent couponCode={invalidCoupon} />
        <ConditionalWrapper
          condition={showTimer}
          wrapper={(children) => (
            <TimerProvider milliseconds={1723000} doubleDigits>
              {children}
            </TimerProvider>
          )}
        >
          <Box overflow="hidden">
            <DealsHeader
              ctaData={headerCta}
              showLanguagePicker={showLanguagePicker}
              showTimer={showTimer}
              newsOutlets={newsOutlets(t)}
            />

            <DealsTimerBar timerBarProps={{ data: timerBar, showTimer }} />

            <ThemeProvider theme={globalTheme}>
              <ShapeWrapper
                shape={() => <Shape.Blob12 color="white" />}
                shapeProps={{
                  height: '100%',
                  zIndex: 0,
                  left: '50%',
                }}
                wrapperProps={{ pb: { _: 48, md: 64, lg: 80 } }}
              >
                <Box position="relative" height="120%" py={32}>
                  <Comparison {...comparison({ t })} />
                </Box>
              </ShapeWrapper>
            </ThemeProvider>

            <FeaturesTabs
              {...featuresTabs({ t, variant: theme.variants?.featuresTabs })}
              sectionProps={{ wrapperProps: { pt: { md: 80 }, pb: { md: 104 } } }}
            />

            <CompatibleDevices
              {...compatibleDevices(t)}
              mobileCard={Card.Device}
              sectionProps={{
                wrapperProps: { my: { _: 24, md: 40 } },
                containerProps: { position: 'relative', zIndex: 1 },
              }}
            />

            <CountryListSection {...countryList({ t })} />

            <ThemeProvider theme={globalTheme}>
              <ContentBlockSection
                {...moneyBackGuarantee(t, language)}
                shape={() => <Shape.Blob6 color="white" />}
                shapeProps={{
                  height: { _: '110%', md: '120%' },
                  left: { _: '90%', md: '55%' },
                }}
                sectionProps={{
                  wrapperProps: { py: { _: 64, md: 88 } },
                  containerProps: {
                    zIndex: 0,
                    position: 'relative',
                  },
                }}
              />
            </ThemeProvider>

            <DealsFooter data={footerCta} showTimer={showTimer} />
          </Box>
        </ConditionalWrapper>
      </Layout>
    </React.Fragment>
  );
};
